<script lang="ts">
  import { onMount } from "svelte"
  import Layout from "$app/Layouts/University.svelte"
  import { Pulse } from "svelte-loading-spinners"
  import Modal from "$app/components/Modal.svelte"
  import { xhr, convertObjectToUrl, toast } from "$app/utils"
  import LibraryVideo from "./LibraryVideo.svelte"
  import VideoForm from "./VideoForm.svelte"
  import ViewVideo from "./ViewVideo.svelte"
  import Playlists from "./Playlists.svelte"
  import { Search, ArrowLeft, X } from "lucide-svelte"
  import { appStore } from "$app/store.svelte"
  import Admin from "$app/Layouts/Admin.svelte"

  let props = $props()

  interface Props {
    plan: PlanType
    library_videos: any[]
    categories: []
    playlists: []
    admin_university: boolean
    filters: any
  }

  let {
    plan,
    categories = [],
    playlists = [],
    admin_university = false
  }: Props = props

  let defaultFilters = {
    plan_filter: "all",
    category: "",
    search_query: "",
    playlist: ""
  }

  let library_videos = $state(props.library_videos || [])
  let filters = $state(props.filters || defaultFilters)

  let openVideoModal = $state(false)
  let openAddVideoModal = $state(false)

  let library_video = $state(null)

  function updatePlanFilter(plan_filter) {
    filters.plan_filter = plan_filter
    filters.playlist = ""
    getVideos(filters, true)
  }

  function updateCategoryFilter(category) {
    filters.category = category
    filters.playlist = ""
    getVideos(filters, true)
  }

  function resetFilters() {
    filters = {
      plan_filter: "all",
      category: "",
      search_query: "",
      playlist: ""
    }
    getVideos(filters, true)
  }

  let currentPlaylist = $state(null)

  let category = null
  let search_query = null

  let isLoading = $state(false)
  let page = 1
  let totalPages = 1

  let vis = true
  function checkInfiniteLoad(): void {
    const totalPageHeight = document.body.scrollHeight
    const scrollPoint = window.scrollY + window.innerHeight + 500

    const visible = scrollPoint >= totalPageHeight

    if (visible && vis) {
      vis = false
      getVideos(filters)
      setTimeout(() => (vis = true), 1000)
    }
  }

  onMount(() => {
    if (filters.playlist) {
      currentPlaylist = playlists.find((p) => p.id == filters.playlist)
    }

    appStore.admin_university = admin_university

    isLoading = true
    getVideos(filters)

    let prev = new Date().getTime()
    let now = 0
    const delay = 10
    window.onscroll = function () {
      now = new Date().getTime()
      if (now - prev > delay) {
        checkInfiniteLoad()
        prev = now
      }
    }
  })

  // Get all statements for this section
  async function getVideos(filter, reset = false): Promise<void> {
    if (reset) {
      totalPages = 0
      page = 1
      library_videos = []
    }
    isLoading = true

    if (totalPages && page > totalPages) {
      isLoading = false
      return
    }

    try {
      const queryStr = convertObjectToUrl(filter)
      const url = `/plans/${appStore.plan.id}/library/filter.json?${queryStr}&page=${page}`
      const resp = await xhr.get(url)
      totalPages = +resp.headers["total-pages"]
      page++

      library_videos = [...library_videos, ...resp.data]

      //window.history.pushState(filter, "Video Library", `?${queryStr}`)
    } catch (e) {
      isLoading = false
      console.log(e)
      toast("There was an error loading the library videos", "error")
    } finally {
      setTimeout(() => {
        //const players = Plyr.setup(".plyr__video-embed")
        isLoading = false
      }, 1000)
    }
  }

  function onViewVideo(video) {
    library_video = video
    openVideoModal = true
  }

  function addedNewVideo() {
    openAddVideoModal = false
    filters = defaultFilters
    filters.plan_filter = "plan"

    getVideos(filters, true)
  }

  function triggerSearch(e) {
    if (e.keyCode === 13) {
      filters.playlist = ""
      getVideos(filters, true)
    }
  }

  function reloadPlaylist(data) {
    filters.playlist = data
    getVideos(filters, true)
  }

  function loadPlaylist(data) {
    currentPlaylist = data
    filters = {
      plan_filter: "all",
      category: "",
      search_query: "",
      playlist: currentPlaylist.id
    }
    getVideos(filters, true)
  }
</script>

<svelte:head>
  <link rel="stylesheet" href="https://cdn.plyr.io/3.7.8/plyr.css" />
</svelte:head>

<Layout {...props}>
  <div
    class="bg-gradient-to-r from-course-bg-light to-course-bg-dark w-full border-t-border-b-2 border-gray-800"
  >
    <div
      class="mx-auto max-w-7xl grid grid-cols-1 md:grid-cols-3 items-center sm:px-6 lg:px-8"
    >
      <div class="flex justify-start items-center">
        <a
          href="/plans/{appStore.plan.id}/university"
          class="flex items-center justify-start hover:border-b hover:font-bold text-white"
        >
          <ArrowLeft class="size-6 text-white mr-1" />
          Back to University
        </a>
      </div>
      <div class="py-3">
        <div class="flex items-center justify-center">
          <img
            class="inline-block h-6 w-6 rounded-full shadow bg-gray-50 p-1"
            src={appStore.plan.logo_url}
            alt="Logo"
          />
          <p
            class="text-base leading-tight text-gray-200 ml-2"
            data-name="plan-name"
          >
            {appStore.plan.name}
          </p>
        </div>
        <h1 class="text-white text-2xl text-center mt-1">Video Library</h1>
      </div>

      <div class="flex justify-end items-center">
        {#if appStore.admin_university}
          <button
            class="py-2 px-8 bg-indigo-600 text-white rounded-lg text-sm hover:bg-indigo-700 hover:font-bold"
            onclick={() => (openAddVideoModal = true)}
            >Add Your Own Video</button
          >
        {/if}
      </div>
    </div>
  </div>

  <div class="max-w-xl mx-auto px-4 sm:px-6 lg:max-w-7xl lg:px-8">
    <div class="grid grid-cols-4 gap-6">
      <div class="bg-white px-4 py-12">
        <div class="grid grid-cols-1 gap-y-6">
          <div>
            <h2 class="text-2xl font-bold">Video Library</h2>
          </div>
          <div>
            <div>
              <label for="search" class="sr-only">Search</label>
              <div class="relative mt-1 rounded-md shadow-sm">
                <div
                  class="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3"
                  aria-hidden="true"
                >
                  <Search class="mr-4 size-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  bind:value={filters.search_query}
                  onkeydown={triggerSearch}
                  onblur={() => getVideos(filters, true)}
                  id="search_query"
                  class="block w-full py-3 text-lg rounded-md border-gray-300 pl-10 focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                  placeholder="Search for Videos"
                />
              </div>
            </div>
          </div>

          <div class="mt-3">
            <h3 class="text-lg font-bold mb-2">Category</h3>
            <select
              id="category"
              onchange={() => updateCategoryFilter(category.value)}
              bind:this={category}
              class="mt-1 block w-full rounded-lg border-gray-300 py-3 pl-3 pr-10 text-lg focus:border-indigo-500 focus:outline-none focus:ring-indigo-500 sm:text-sm"
            >
              <option value="">Select Category</option>
              {#each categories as category}
                {#if category == filters.category}
                  <option selected value={category}>{category}</option>
                {:else}
                  <option value={category}>{category}</option>
                {/if}
              {/each}
            </select>
          </div>

          <div class="mt-4">
            <Playlists {playlists} onLoadPlaylist={loadPlaylist} />
          </div>

          <div>
            {#if appStore.admin_university}
              <div class="border-t border-gray-200 mt-6">
                <div class="mt-6 flex flex-col justify-center px-2 xl:px-6">
                  <p class="text-lg font-bold mx-auto px-2 xl:px-6 text-center">
                    Don't see a video listed?
                  </p>

                  <button
                    class="mt-2 py-3 px-4 xl:px-8 bg-indigo-600 text-white rounded-lg text-sm hover:bg-indigo-700 hover:font-bold"
                    onclick={() => (openAddVideoModal = true)}
                    >Add Your Own Video</button
                  >
                </div>
              </div>
            {/if}
          </div>
        </div>
      </div>
      <div class="col-span-3 pr-4">
        {#if filters.playlist}
          <section class="max-w-3xl mx-auto lg:max-w-7xl py-6">
            <button
              type="button"
              onclick={resetFilters}
              class="bg-lwp-blue text-gray-200 rounded-lg py-3 px-4 rounded-lg text-base flex items-center"
            >
              <ArrowLeft class="w-5 h-5 ml-1" />
              Back to All Videos
            </button>
          </section>
        {:else}
          <section class="max-w-3xl mx-auto lg:max-w-7xl py-6">
            <div class="grid grid-cols-2">
              <div class="flex items-center justify-start">
                <div
                  class="bg-lwp-blue text-gray-200 rounded-lg flex p-1 gap-x-1"
                  id="tab-selector"
                >
                  <button
                    type="button"
                    onclick={() => updatePlanFilter("all")}
                    class="p-2 px-4 rounded-lg text-base"
                    class:active={filters.plan_filter === "all"}
                  >
                    All Videos
                  </button>
                  <button
                    type="button"
                    onclick={() => updatePlanFilter("plan")}
                    class="p-2 px-4 rounded-lg text-base"
                    class:active={filters.plan_filter === "plan"}
                  >
                    My Plan's Videos
                  </button>
                </div>
              </div>
              <div class="flex items-center justify-end"></div>
            </div>
          </section>
          {#if filters.category.length > 0 || filters.search_query.length > 0}
            <section class="max-w-3xl mx-auto lg:max-w-7xl py-2">
              <button
                type="button"
                onclick={resetFilters}
                class="bg-blue-800 text-gray-200 rounded-lg py-2 px-4 rounded-lg text-sm flex items-center"
              >
                <X class="size-4 ml-1" />
                Reset Filters
              </button>
            </section>
          {/if}
        {/if}

        <section class="max-w-3xl mx-auto lg:max-w-7xl py-6">
          {#if filters.category.length > 0}
            <h2 class="text-3xl font-bold mb-4">
              <span class="text-gray-600 text-xl">Category:</span>
              {filters.category}
            </h2>
          {/if}
          {#if filters.search_query.length > 0}
            <h2 class="text-3xl font-bold mb-4">
              <span class="text-gray-600 text-xl">Search Query:</span>
              {filters.search_query}
            </h2>
          {/if}
          {#if filters.playlist.length > 0}
            <h2 class="text-3xl font-bold mb-4">
              <span class="text-gray-600 text-xl">Playlist:</span>
              {currentPlaylist.title}
            </h2>
          {/if}
          <div
            class="grid grid-cols-3 gap-6"
            data-name="video-list"
            id="infinite-scroll"
          >
            {#each library_videos as library_video}
              <LibraryVideo {library_video} {onViewVideo} />
            {/each}

            {#if isLoading === false && library_videos?.length === 0}
              <li
                class="bg-white sm:flex sm:space-x-5 p-6 border-gray-100 rounded-lg"
              >
                <p class="font-lg font-bold text-gray-900 mb-3">
                  {#if filters.playlist}
                    No videos in this playlist.
                  {:else}
                    No videos found that match your filters.
                  {/if}
                </p>
              </li>
            {/if}
          </div>
          {#if isLoading}
            <div class="py-6 w-full">
              <div class="text-center flex justify-center">
                <Pulse size="140" color="#cdcdcd" unit="px" duration="1s" />
              </div>
            </div>
          {/if}
        </section>
      </div>
    </div>
  </div>
</Layout>

<Modal bind:open={openVideoModal} large={true}>
  <ViewVideo {library_video} onReloadPlaylist={reloadPlaylist} />
</Modal>

<Modal bind:open={openAddVideoModal}>
  <VideoForm {categories} onAddedNewVideo={addedNewVideo} />
</Modal>

<style lang="css">
  #tab-selector button {
    font-weight: 400;
    outline: none;
    transition: background-color 200ms ease-in-out;
  }
  #tab-selector button.active {
    background-color: white;
    color: #222;
    font-weight: 700;
    z-index: 5;
  }
</style>
